import type { ComponentProps } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Badge, {
  type BadgeVariant,
} from "@/v2-backoffice-client/components/badge"
import Link from "@/v2-backoffice-client/components/link"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import LicenseGoverningBadge from "@/v2-backoffice-client/license/LicenseGoverningBadge"
import { formatLicenseDeniedReason } from "@/v2-backoffice-client/usage/denied/usage.denied.constants"
import { buildCustomerMcpLink } from "@/v2-backoffice-client/portfolio/portfolio.utils"
import {
  deriveMcpRefusedRead,
  type PortfolioMcpCustomer,
  type PortfolioMcpRefusedRead,
} from "@/v2-backoffice-client/portfolio/portfolio.mcp.utils"

export type PortfolioMcpRefusedTableProps = ComponentProps<"div"> & {
  /** Customers with refused sign-ins in the window, most attempts first. */
  customers: PortfolioMcpCustomer[]
  licenseStatusNamesByCode: Map<number, string>
}

const READ_VARIANTS: Record<PortfolioMcpRefusedRead["tone"], BadgeVariant> = {
  positive: "positive",
  warning: "warning",
  neutral: "neutral",
}

// Every customer whose people were refused by the MCP license gate in the
// last 28 days, most attempts first, with the reason, the first and last
// attempt, the MCP license they hold today and where that leaves them —
// back in use, licensed but not back, or still outside. The one-off report
// had to dig this out of traces by hand; here it is the table to open the
// license conversation from.
function PortfolioMcpRefusedTable(props: PortfolioMcpRefusedTableProps) {
  const { customers, licenseStatusNamesByCode, className, ...divProps } = props

  return (
    <div className={cn("overflow-x-auto", className)} {...divProps}>
      <table className="usage-report-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th className="num">People</th>
            <th className="num">Attempts</th>
            <th>Reason</th>
            <th>First</th>
            <th>Last</th>
            <th>MCP license today</th>
            <th>Where they stand</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((customer) => {
            const read = deriveMcpRefusedRead(customer)
            return (
              <tr key={customer.ExternalCustomerId}>
                <td>
                  <Link
                    to={buildCustomerMcpLink(customer.ExternalCustomerId)}
                    iconSrc={null}
                    className="block max-w-56 truncate font-medium"
                  >
                    {customer.Name}
                  </Link>
                </td>
                <td className="num">{customer.DeniedUsers28d}</td>
                <td className="num">{formatNumber(customer.Denied28d)}</td>
                <td>{formatLicenseDeniedReason(customer.DeniedReason)}</td>
                <td className="whitespace-nowrap text-muted-foreground">
                  {customer.FirstDeniedAt
                    ? formatDateString(customer.FirstDeniedAt)
                    : "—"}
                </td>
                <td className="whitespace-nowrap text-muted-foreground">
                  {customer.LastDeniedAt ? (
                    <RelativeDate date={customer.LastDeniedAt} />
                  ) : (
                    "—"
                  )}
                </td>
                <td className="whitespace-nowrap">
                  <LicenseGoverningBadge
                    size="xs"
                    license={{
                      LicenseStatusCode: customer.LicenseStatusCode,
                      LicenseTrialEndsAt: customer.LicenseTrialEndsAt,
                      LicensePlanNames: customer.LicensePlanNames,
                      LicensedSeats: customer.LicensedSeats,
                      LicenseRenewsAt: customer.LicenseRenewsAt,
                      LicenseEndedAt: customer.LicenseEndedAt,
                    }}
                    statusName={licenseStatusNamesByCode.get(
                      customer.LicenseStatusCode,
                    )}
                  />
                </td>
                <td>
                  <Badge variant={READ_VARIANTS[read.tone]} size="xs">
                    {read.label}
                  </Badge>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default PortfolioMcpRefusedTable
